import React, { useState, useEffect } from "react";
import { Link2, Plus, Edit, Trash2, CheckCircle, Clock, XCircle, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import AppLayout from "../../components/AppLayout";

export default function DirectoryTracker() {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, live: 0, pending: 0, rejected: 0, by_category: {} });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    site_name: "",
    site_url: "",
    category: "directory",
    submission_date: new Date().toISOString().split('T')[0],
    status: "pending",
    backlink_url: "",
    contact_info: "",
    notes: "",
    renewal_date: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [submissionsRes, statsRes] = await Promise.all([
        api.get("/api/directory-tracker"),
        api.get("/api/directory-tracker/stats"),
      ]);
      setSubmissions(submissionsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Failed to load directory tracker");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/api/directory-tracker/${editingId}`, formData);
        toast.success("Submission updated");
      } else {
        await api.post("/api/directory-tracker", formData);
        toast.success("Submission added");
      }
      setShowModal(false);
      setEditingId(null);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const handleEdit = (submission) => {
    setEditingId(submission.id);
    setFormData({
      site_name: submission.site_name,
      site_url: submission.site_url,
      category: submission.category,
      submission_date: submission.submission_date?.split('T')[0] || "",
      status: submission.status,
      backlink_url: submission.backlink_url || "",
      contact_info: submission.contact_info || "",
      notes: submission.notes || "",
      renewal_date: submission.renewal_date?.split('T')[0] || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this submission?")) return;
    try {
      await api.delete(`/api/directory-tracker/${id}`);
      toast.success("Submission deleted");
      loadData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setFormData({
      site_name: "",
      site_url: "",
      category: "directory",
      submission_date: new Date().toISOString().split('T')[0],
      status: "pending",
      backlink_url: "",
      contact_info: "",
      notes: "",
      renewal_date: "",
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      live: { bg: "bg-green-500/20", text: "text-green-400", icon: CheckCircle },
      pending: { bg: "bg-yellow-500/20", text: "text-yellow-400", icon: Clock },
      rejected: { bg: "bg-red-500/20", text: "text-red-400", icon: XCircle },
      expired: { bg: "bg-zinc-500/20", text: "text-zinc-400", icon: RefreshCw },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon size={14} />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">📋 Directory & Forum Tracker</h1>
          <p className="text-zinc-400">Track submissions to adult directories, forums, and listing sites</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="p-6 rounded-xl border bg-zinc-900/50" style={{ borderColor: "rgba(113, 113, 122, 0.2)" }}>
            <div className="text-sm text-zinc-400 mb-1">Total Submissions</div>
            <div className="text-3xl font-bold text-white">{stats.total}</div>
          </div>
          <div className="p-6 rounded-xl border bg-zinc-900/50" style={{ borderColor: "rgba(34, 197, 94, 0.2)" }}>
            <div className="text-sm text-zinc-400 mb-1">Live</div>
            <div className="text-3xl font-bold text-green-400">{stats.live}</div>
          </div>
          <div className="p-6 rounded-xl border bg-zinc-900/50" style={{ borderColor: "rgba(234, 179, 8, 0.2)" }}>
            <div className="text-sm text-zinc-400 mb-1">Pending</div>
            <div className="text-3xl font-bold text-yellow-400">{stats.pending}</div>
          </div>
          <div className="p-6 rounded-xl border bg-zinc-900/50" style={{ borderColor: "rgba(239, 68, 68, 0.2)" }}>
            <div className="text-sm text-zinc-400 mb-1">Rejected</div>
            <div className="text-3xl font-bold text-red-400">{stats.rejected}</div>
          </div>
        </div>

        {/* Add Button */}
        <div className="mb-6">
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition inline-flex items-center gap-2">
            <Plus size={18} />
            Add Submission
          </button>
        </div>

        {/* Submissions Table */}
        <div className="bg-zinc-900/50 border rounded-xl overflow-hidden" style={{ borderColor: "rgba(113, 113, 122, 0.2)" }}>
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ borderColor: "rgba(113, 113, 122, 0.2)" }}>
                <th className="text-left p-4 text-sm font-semibold text-zinc-400">Site</th>
                <th className="text-left p-4 text-sm font-semibold text-zinc-400">Category</th>
                <th className="text-left p-4 text-sm font-semibold text-zinc-400">Status</th>
                <th className="text-left p-4 text-sm font-semibold text-zinc-400">Submission Date</th>
                <th className="text-left p-4 text-sm font-semibold text-zinc-400">Backlink</th>
                <th className="text-right p-4 text-sm font-semibold text-zinc-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-zinc-500">
                    No submissions yet. Click "Add Submission" to get started.
                  </td>
                </tr>
              ) : (
                submissions.map((sub) => (
                  <tr key={sub.id} className="border-b hover:bg-zinc-800/30 transition" style={{ borderColor: "rgba(113, 113, 122, 0.1)" }}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Link2 size={16} className="text-purple-400" />
                        <a href={sub.site_url} target="_blank" rel="noopener noreferrer" className="text-white hover:text-purple-400 transition">
                          {sub.site_name}
                        </a>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-zinc-300 capitalize">{sub.category}</span>
                    </td>
                    <td className="p-4">
                      {getStatusBadge(sub.status)}
                    </td>
                    <td className="p-4 text-sm text-zinc-400">
                      {new Date(sub.submission_date).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      {sub.backlink_url ? (
                        <a href={sub.backlink_url} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline inline-flex items-center gap-1">
                          <ExternalLink size={14} />
                          View
                        </a>
                      ) : (
                        <span className="text-zinc-600 text-sm">-</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => handleEdit(sub)}
                          className="p-2 hover:bg-zinc-700 rounded-lg transition text-zinc-400 hover:text-white">
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(sub.id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition text-zinc-400 hover:text-red-400">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-2xl bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-white mb-6">
                {editingId ? "Edit Submission" : "Add New Submission"}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Site Name *</label>
                    <input
                      type="text"
                      value={formData.site_name}
                      onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
                      className="w-full px-4 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Site URL *</label>
                    <input
                      type="url"
                      value={formData.site_url}
                      onChange={(e) => setFormData({ ...formData, site_url: e.target.value })}
                      className="w-full px-4 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white">
                      <option value="directory">Directory</option>
                      <option value="forum">Forum</option>
                      <option value="review_site">Review Site</option>
                      <option value="listing">Listing</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Status *</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white">
                      <option value="pending">Pending</option>
                      <option value="live">Live</option>
                      <option value="rejected">Rejected</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Submission Date *</label>
                    <input
                      type="date"
                      value={formData.submission_date}
                      onChange={(e) => setFormData({ ...formData, submission_date: e.target.value })}
                      className="w-full px-4 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Renewal Date</label>
                    <input
                      type="date"
                      value={formData.renewal_date}
                      onChange={(e) => setFormData({ ...formData, renewal_date: e.target.value })}
                      className="w-full px-4 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Backlink URL</label>
                  <input
                    type="url"
                    value={formData.backlink_url}
                    onChange={(e) => setFormData({ ...formData, backlink_url: e.target.value })}
                    className="w-full px-4 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Contact Info</label>
                  <input
                    type="text"
                    value={formData.contact_info}
                    onChange={(e) => setFormData({ ...formData, contact_info: e.target.value })}
                    className="w-full px-4 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white"
                    placeholder="Email, phone, or username"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white"
                    rows="3"
                    placeholder="Additional notes..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition">
                    {editingId ? "Update" : "Add"} Submission
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); setEditingId(null); resetForm(); }}
                    className="px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-lg transition">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
